import { Delete, Sync } from '@mui/icons-material';
import { AlertColor, Box, Stack, Typography } from '@mui/material';
import { DataGridPro, GridRowId } from '@mui/x-data-grid-pro';

import Alert from '@/components/alert';
import Button from '@/components/button';
import { Creator } from '@/models/creator';

import { useCreatorGrid } from '../hooks/useCreatorGrid';
import { buildPayload } from '../utils/creatorImport.utils';

interface Step2Props {
  fileValidation: Creator.FileValidation | null;
  validationError: string | string[] | null;
  isValidated: boolean;
  onValidate: (payload: Creator.BulkCreatePayload) => void;
  isValidating: boolean;
}

const renderAlertContent = (content: string | string[]) =>
  Array.isArray(content) ? (
    <Stack>
      {content.map((item, i) => (
        <Typography key={i}>{item}</Typography>
      ))}
    </Stack>
  ) : (
    content
  );

const hasFileWarnings = (fileValidation: Creator.FileValidation | null) =>
  fileValidation?.creators.some(
    ({ creatorWarnings }) =>
      (creatorWarnings?.wrong.length ?? 0) > 0 ||
      (creatorWarnings?.duplicates.length ?? 0) > 0 ||
      creatorWarnings?.hasInvalidCreatorName === true
  ) ?? false;

const Step2 = ({
  fileValidation,
  validationError,
  isValidated,
  onValidate,
  isValidating
}: Step2Props) => {
  const {
    rows,
    columns,
    rowSelectionModel,
    setRowSelectionModel,
    rowModesModel,
    setRowModesModel,
    hasInvalidRows,
    handleDeleteSelected,
    handleRowEditStop,
    handleColumnWidthChange,
    processRowUpdate
  } = useCreatorGrid(fileValidation?.creators ?? []);

  const hasErrors = !!validationError && !Array.isArray(validationError);
  const hasWarnings = !hasErrors && hasFileWarnings(fileValidation);

  const alertConfig: { severity: AlertColor; content: string | string[] } =
    validationError
      ? { severity: 'warning', content: validationError }
      : { severity: 'success', content: 'No errors found.' };

  const handleValidate = () => {
    if (!fileValidation || hasInvalidRows) return;
    onValidate(buildPayload(fileValidation.creators, rows));
  };

  const showWarningGrid = (hasWarnings || hasInvalidRows) && !isValidated;

  return (
    <Stack gap={2} ml={2}>
      <Box display="flex" gap={2} flexDirection="column">
        <Typography variant="h6" fontSize={20} fontWeight={500}>
          Verification Report
        </Typography>
        <Alert
          severity={alertConfig.severity}
          content={renderAlertContent(alertConfig.content)}
          sx={{ '& .MuiAlert-icon': { alignItems: 'center' } }}
        />
      </Box>
      {showWarningGrid && (
        <>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              variant="text"
              text="Validate"
              loading={isValidating}
              disabled={hasInvalidRows}
              onClick={handleValidate}
              endIcon={<Sync />}
            />
            <Button
              variant="text"
              text="Delete"
              disabled={rowSelectionModel.length === 0}
              onClick={handleDeleteSelected}
              endIcon={<Delete />}
            />
          </Box>
          <DataGridPro
            rows={rows}
            columns={columns}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            onRowEditStop={handleRowEditStop}
            onColumnWidthChange={handleColumnWidthChange}
            onRowSelectionModelChange={(ids) =>
              setRowSelectionModel(ids as GridRowId[])
            }
            processRowUpdate={processRowUpdate}
            checkboxSelection
            disableRowSelectionOnClick
            autoHeight
            pagination
            pageSizeOptions={[5, 10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
            sx={{
              '& .unresolved-account-cell': {
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
                color: 'error.main'
              }
            }}
          />
        </>
      )}
    </Stack>
  );
};

export default Step2;
